import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { MessageSquare, Mail, MessageCircle } from "lucide-react";
import { MessageFormValues } from "@/components/cashback/message-utils/types";
import { useSmsTemplates } from "@/hooks/useSmsTemplates";
import { previewSmsMessage } from "@/utils/smsUtils";

interface MessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (values: MessageFormValues) => void;
  currentMessage: {
    id: string;
    sendTime: string;
    message: string;
    channels?: ("whatsapp" | "sms" | "email")[];
    isActive?: boolean;
    smsTemplateId?: string;
  } | null;
  disabled?: boolean;
  moduleType?: 'cashback' | 'referrals';
}

const MessageDialog = ({ open, onOpenChange, onSave, currentMessage, disabled = false, moduleType = 'cashback' }: MessageDialogProps) => {
  const { templates } = useSmsTemplates();
  const [showPreview, setShowPreview] = useState(false);
  
  const form = useForm<MessageFormValues>({
    defaultValues: {
      sendTime: currentMessage?.sendTime || "",
      message: currentMessage?.message || "",
      channels: currentMessage?.channels || ["email"],
      isActive: currentMessage?.isActive !== undefined ? currentMessage.isActive : true,
      useCustomMessage: !currentMessage?.smsTemplateId,
      smsTemplateId: currentMessage?.smsTemplateId
    }
  });

  // Reset form when dialog opens with new values
  useEffect(() => {
    if (open) {
      form.reset({
        sendTime: currentMessage?.sendTime || "",
        message: currentMessage?.message || "",
        channels: currentMessage?.channels || ["email"],
        isActive: currentMessage?.isActive !== undefined ? currentMessage.isActive : true,
        useCustomMessage: !currentMessage?.smsTemplateId,
        smsTemplateId: currentMessage?.smsTemplateId
      });
    }
  }, [open, currentMessage, form]);

  const insertVariable = (variable: string) => {
    const currentMessage = form.getValues("message");
    form.setValue("message", `${currentMessage} {{${variable}}}`);
  };

  const toggleChannel = (channel: "whatsapp" | "sms" | "email") => {
    const currentChannels = form.getValues("channels") || [];
    
    if (currentChannels.includes(channel)) {
      // Remove channel if it exists
      if (currentChannels.length > 1) { // Prevent removing all channels
        form.setValue("channels", currentChannels.filter(ch => ch !== channel));
      }
    } else {
      // Add channel if it doesn't exist
      form.setValue("channels", [...currentChannels, channel]);
    }
  };

  const getPreviewMessage = () => {
    const channels = form.watch("channels") || [];
    const useCustom = form.watch("useCustomMessage");
    const templateId = form.watch("smsTemplateId");
    const customMessage = form.watch("message");

    if (!channels.includes("sms")) {
      return customMessage;
    }

    if (useCustom) {
      return customMessage;
    }

    const template = templates.find(t => t.id === templateId) || templates.find(t => t.is_default);
    if (template) {
      return previewSmsMessage(template.message);
    }

    return customMessage;
  };

  const getVariableOptions = () => {
    const baseVariables = [
      { key: "customer_name", label: "Nome do cliente" },
      { key: "coupon_code", label: "Código do cupom" },
    ];

    if (moduleType === 'cashback') {
      return [
        ...baseVariables,
        { key: "bonus_value", label: "Valor do bônus" },
        { key: "min_purchase", label: "Valor mínimo de compra" },
        { key: "validity_days", label: "Dias de validade" },
      ];
    } else {
      return [
        ...baseVariables,
        { key: "referral_bonus", label: "Bônus de indicação" },
        { key: "discount_value", label: "Valor do desconto" },
      ];
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle>
            {currentMessage ? "Editar mensagem automática" : "Nova mensagem automática"}
          </DialogTitle>
          <DialogDescription>
            Configure quando e o que será enviado para seus clientes
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSave)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="send-time">Tempo após a compra</Label>
              <Select 
                value={form.watch("sendTime")} 
                onValueChange={(value) => form.setValue("sendTime", value)}
                disabled={disabled}
              >
                <SelectTrigger id="send-time">
                  <SelectValue placeholder="Selecione quando enviar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1h">1 hora</SelectItem>
                  <SelectItem value="6h">6 horas</SelectItem>
                  <SelectItem value="12h">12 horas</SelectItem>
                  <SelectItem value="1">1 dia</SelectItem>
                  <SelectItem value="3">3 dias</SelectItem>
                  <SelectItem value="10">10 dias</SelectItem>
                  <SelectItem value="15">15 dias</SelectItem>
                  <SelectItem value="20">20 dias</SelectItem>
                  <SelectItem value="25">25 dias</SelectItem>
                  <SelectItem value="27">27 dias</SelectItem>
                  <SelectItem value="28">28 dias</SelectItem>
                  <SelectItem value="29">29 dias</SelectItem>
                  <SelectItem value="30">30 dias</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Switch 
                  id="isActive" 
                  checked={form.watch("isActive")}
                  onCheckedChange={(value) => form.setValue("isActive", value)}
                  disabled={disabled}
                />
                <Label htmlFor="isActive">Mensagem ativa</Label>
              </div>
              <p className="text-xs text-muted-foreground">
                Mensagens inativas não serão enviadas automaticamente
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="channels">Canais de envio</Label>
              <ToggleGroup 
                type="multiple" 
                className="justify-start border rounded-md p-1"
                value={form.watch("channels")}
                disabled={disabled}
              >
                <ToggleGroupItem 
                  value="email" 
                  onClick={() => !disabled && toggleChannel("email")}
                  className={`gap-2 ${form.watch("channels")?.includes("email") ? "bg-blue-50 text-blue-700" : ""}`}
                  disabled={disabled}
                >
                  <Mail className="h-4 w-4" />
                  <span>Email</span>
                </ToggleGroupItem>
                <ToggleGroupItem 
                  value="whatsapp" 
                  onClick={() => !disabled && toggleChannel("whatsapp")}
                  className={`gap-2 ${form.watch("channels")?.includes("whatsapp") ? "bg-green-50 text-green-700" : ""}`}
                  disabled={disabled}
                >
                  <MessageCircle className="h-4 w-4" />
                  <span>WhatsApp</span>
                </ToggleGroupItem>
                <ToggleGroupItem 
                  value="sms" 
                  onClick={() => !disabled && toggleChannel("sms")}
                  className={`gap-2 ${form.watch("channels")?.includes("sms") ? "bg-purple-50 text-purple-700" : ""}`}
                  disabled={disabled}
                >
                  <MessageSquare className="h-4 w-4" />
                  <span>SMS</span>
                </ToggleGroupItem>
              </ToggleGroup>
              <p className="text-xs text-muted-foreground">
                Selecione em quais canais esta notificação deve ser enviada
              </p>
            </div>

            {/* SMS Template Configuration - only shown when SMS is selected */}
            {form.watch("channels")?.includes("sms") && (
              <div className="space-y-3 p-4 border rounded-lg">
                <Label className="text-sm font-medium">Configuração SMS</Label>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={form.watch("useCustomMessage")}
                    onCheckedChange={(value) => form.setValue("useCustomMessage", value)}
                    disabled={disabled}
                  />
                  <Label className="text-sm">Usar mensagem personalizada</Label>
                </div>

                {!form.watch("useCustomMessage") && (
                  <div className="space-y-2">
                    <Label htmlFor="sms-template">Template SMS</Label>
                    <Select
                      value={form.watch("smsTemplateId") || ""}
                      onValueChange={(value) => form.setValue("smsTemplateId", value)}
                      disabled={disabled}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um template" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.name} {template.is_default && "(Padrão)"}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label htmlFor="message">
                  {form.watch("channels")?.includes("sms") && !form.watch("useCustomMessage") 
                    ? "Mensagem (Email/WhatsApp)" 
                    : "Mensagem"}
                </Label>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" disabled={disabled}>Inserir variável</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    {getVariableOptions().map((variable) => (
                      <DropdownMenuItem key={variable.key} onClick={() => insertVariable(variable.key)}>
                        {variable.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <Textarea 
                id="message"
                value={form.watch("message")}
                onChange={(e) => form.setValue("message", e.target.value)}
                placeholder="Digite sua mensagem aqui. Use as variáveis para personalizar."
                className="min-h-[120px]"
                disabled={disabled}
              />
              <p className="text-sm text-muted-foreground">
                As variáveis serão substituídas pelos valores reais quando a mensagem for enviada.
              </p>
            </div>

            {/* Preview */}
            {form.watch("channels")?.includes("sms") && (
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPreview(!showPreview)}
                    disabled={disabled}
                  >
                    {showPreview ? "Ocultar" : "Mostrar"} preview SMS
                  </Button>
                </div>
                
                {showPreview && (
                  <div className="p-3 bg-muted rounded-md">
                    <p className="text-sm font-medium mb-2">Preview SMS:</p>
                    <p className="text-sm whitespace-pre-wrap">{getPreviewMessage()}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      Caracteres: {getPreviewMessage().length}/160
                    </p>
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button variant="brand" type="submit" disabled={disabled}>Salvar</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default MessageDialog;